import {
  ChildTablePermissionsFormData,
  StructuredRoleFormData,
  TableFullPermissionFormData,
  tableFullPermissionFormSchema,
} from '@doss/common-all'
import { CheckedState } from '@radix-ui/react-checkbox'
import { get, sortBy } from 'lodash'
import {
  PermissionInput,
  PermissionObjectType,
  PermissionType,
} from '~/common/generated/apollo-operations'
import {
  BasicPermissionType,
  FormPermissionType,
  ManualTrigger,
  PermissionsTabEmbed,
  PermissionsTabEndpoint,
  PermissionsTabFormUnified,
  PermissionsTabTable,
} from '../roles/types'
import { Permission } from '../settings/types'
import { SimpleTable } from '../table-columns/types'
import {
  TableColumnPermission,
  TableTabPermission,
  filterByType,
  isEmbedPermission,
  isEndpointPermission,
  isFormPermission,
  isFullTablePermission,
  isGeneralTablePermission,
  isManualTriggerPermission,
  isPermission,
  isTableColumnPermission,
  isTableTabPermission,
  isWorkspacePermission,
} from './types'

type PermissionsObject = Record<string, Record<string, CheckedState>>

export function getPermissionState<T extends string>(
  permissions: PermissionsObject,
  permissionType: T,
): CheckedState {
  let totalCount = 0 // The total number of permissions
  let trueCount = 0 // The number of permissions that are true
  let falseCount = 0 // The number of permissions that are false
  let indeterminateCount = 0 // The number of permissions that are indeterminate

  for (const key in permissions) {
    if (Object.prototype.hasOwnProperty.call(permissions, key)) {
      totalCount++
      const state = permissions[key][permissionType]

      if (state === true) {
        trueCount++
      } else if (state === false) {
        falseCount++
      } else {
        indeterminateCount++
      }
    }
  }

  if (totalCount === 0) {
    return false
  }

  // If ANY item is indeterminate, the whole thing is indeterminate
  if (indeterminateCount > 0) {
    return 'indeterminate'
  }

  // All are true
  if (trueCount === totalCount) {
    return true
  }

  // All are false
  if (falseCount === totalCount) {
    return false
  }

  // Mixed true/false
  return 'indeterminate'
}

/**
 * Checks if a table effectively has view permission by having all columns have view and
 * all tabs have view permission
 */
export function hasEffectiveTableViewPermission(
  tableData: TableFullPermissionFormData | ChildTablePermissionsFormData,
): boolean {
  // Check if ALL columns have view permission
  const columnPermissions = tableData.columnPermissions
  const tabPermissions = tableData.tabPermissions
  // A table effectively has view permission if ALL columns AND ALL tabs have view permission
  const columnPermissionState = getPermissionState(columnPermissions, BasicPermissionType.view)
  const tabPermissionState = getPermissionState(tabPermissions, BasicPermissionType.view)

  return columnPermissionState === true && tabPermissionState === true
}

/**
 * Checks if a table effectively has edit permission by having all columns have edit and
 * all tabs have view permission
 */
export function hasEffectiveTableEditPermission(
  tableData: TableFullPermissionFormData | ChildTablePermissionsFormData,
): boolean {
  const columnPermissions = tableData.columnPermissions
  const tabPermissions = tableData.tabPermissions
  // A table effectively has edit permission if ALL columns have edit
  // AND ALL tabs have view permission
  const columnPermissionState = getPermissionState(columnPermissions, BasicPermissionType.edit)
  const tabPermissionState = getPermissionState(tabPermissions, BasicPermissionType.view)

  return columnPermissionState === true && tabPermissionState === true
}

/**
 * Converts the table related form data for creating or updating a role into an array
 * of permission inputs, filtering out redundant inputs for tables. This does not include
 * child table permissions that are not determined by the parent table permissions.
 */
export function convertParentTableFormDataToPermissionInputs(
  tableId: string,
  permissions: TableFullPermissionFormData,
  permissionInputs: PermissionInput[],
): void {
  const tablePerms = permissions.tablePermissions

  const hasTableViewPermission = hasEffectiveTableViewPermission(permissions)
  const hasTableEditPermission = hasEffectiveTableEditPermission(permissions)

  // Handle table permissions first
  if (hasTableViewPermission) {
    permissionInputs.push({
      permissionObjectType: PermissionObjectType.TABLE,
      permissionType: PermissionType.VIEW_ALL_TABLE_CELLS,
      permissionObjectId: tableId,
    })
  }

  if (hasTableEditPermission) {
    permissionInputs.push({
      permissionObjectType: PermissionObjectType.TABLE,
      permissionType: PermissionType.EDIT_ALL_TABLE_CELLS,
      permissionObjectId: tableId,
    })
  }

  if (tablePerms.addRow) {
    permissionInputs.push({
      permissionObjectType: PermissionObjectType.TABLE,
      permissionType: PermissionType.ADD_ROW,
      permissionObjectId: tableId,
    })

    // Add add row permission for all child tables
    for (const childTableId in permissions.childTables) {
      permissionInputs.push({
        permissionObjectId: childTableId,
        permissionObjectType: PermissionObjectType.TABLE,
        permissionType: PermissionType.ADD_ROW,
      })
    }
  }

  if (tablePerms.deleteRow) {
    permissionInputs.push({
      permissionObjectType: PermissionObjectType.TABLE,
      permissionType: PermissionType.DELETE_ROW,
      permissionObjectId: tableId,
    })
    // Add delete row permission for all child tables
    for (const childTableId in permissions.childTables) {
      permissionInputs.push({
        permissionObjectId: childTableId,
        permissionObjectType: PermissionObjectType.TABLE,
        permissionType: PermissionType.DELETE_ROW,
      })
    }
  }

  if (tablePerms.configureTabs) {
    permissionInputs.push({
      permissionObjectType: PermissionObjectType.TABLE,
      permissionType: PermissionType.CONFIGURE_TABLE_TABS,
      permissionObjectId: tableId,
    })

    // Add manage tabs permission for all child tables
    for (const childTableId in permissions.childTables) {
      permissionInputs.push({
        permissionObjectId: childTableId,
        permissionObjectType: PermissionObjectType.TABLE,
        permissionType: PermissionType.CONFIGURE_TABLE_TABS,
      })
    }
  }

  if (tablePerms.configurePivotTables) {
    permissionInputs.push({
      permissionObjectType: PermissionObjectType.TABLE,
      permissionType: PermissionType.CONFIGURE_PIVOT_TABLES,
      permissionObjectId: tableId,
    })

    // Add manage pivot tables permission for all child tables
    for (const childTableId in permissions.childTables) {
      permissionInputs.push({
        permissionObjectId: childTableId,
        permissionObjectType: PermissionObjectType.TABLE,
        permissionType: PermissionType.CONFIGURE_PIVOT_TABLES,
      })
    }
  }

  // Only add column permissions if no table-level permissions exist
  if (!hasTableViewPermission || !hasTableEditPermission) {
    for (const columnId in permissions.columnPermissions) {
      const columnData = get(permissions.columnPermissions, columnId, undefined)
      if (!hasTableViewPermission && !!columnData?.view) {
        permissionInputs.push({
          permissionObjectType: PermissionObjectType.TABLE_COLUMN,
          permissionType: PermissionType.VIEW_COLUMN,
          permissionObjectId: columnId,
        })
      }

      if (!hasTableEditPermission && !!columnData?.edit) {
        permissionInputs.push({
          permissionObjectType: PermissionObjectType.TABLE_COLUMN,
          permissionType: PermissionType.EDIT_CELL,
          permissionObjectId: columnId,
        })
      }
    }

    // Only add tab permissions if no table-level permissions exist
    for (const tabId in permissions.tabPermissions) {
      const tabData = get(permissions.tabPermissions, tabId, undefined)
      if (!hasTableViewPermission && !!tabData?.view) {
        permissionInputs.push({
          permissionObjectType: PermissionObjectType.TABLE_TAB,
          permissionType: PermissionType.VIEW_TABLE_TAB_ROWS,
          permissionObjectId: tabId,
        })
      }
    }
  }
}

export function processChildTablePermissions(
  childTables: Record<string, ChildTablePermissionsFormData>,
  permissionInputs: PermissionInput[],
  options: {
    // True if on the full role form, false if on the table column field permissions form
    includeAllPermissions?: boolean
  },
): void {
  for (const childTableId in childTables) {
    const childTablePerms = get(childTables, childTableId, undefined)
    if (!childTablePerms) {
      continue
    }

    const hasFullTableViewPermission = hasEffectiveTableViewPermission(childTablePerms)
    const hasFullTableEditPermission = hasEffectiveTableEditPermission(childTablePerms)

    if (hasFullTableViewPermission) {
      permissionInputs.push({
        permissionObjectType: PermissionObjectType.TABLE,
        permissionType: PermissionType.VIEW_ALL_TABLE_CELLS,
        permissionObjectId: childTableId,
      })
    }

    if (hasFullTableEditPermission) {
      permissionInputs.push({
        permissionObjectType: PermissionObjectType.TABLE,
        permissionType: PermissionType.EDIT_ALL_TABLE_CELLS,
        permissionObjectId: childTableId,
      })
    }

    // Only include detailed permissions if requested (for the full role form data)
    if (options.includeAllPermissions) {
      // Only add column permissions if no table-level permissions exist
      if (!hasFullTableViewPermission || !hasFullTableEditPermission) {
        for (const columnId in childTablePerms.columnPermissions) {
          const columnData = get(childTablePerms.columnPermissions, columnId, undefined)
          if (!columnData) {
            continue
          }

          if (!hasFullTableViewPermission && columnData.view) {
            permissionInputs.push({
              permissionObjectType: PermissionObjectType.TABLE_COLUMN,
              permissionType: PermissionType.VIEW_COLUMN,
              permissionObjectId: columnId,
            })
          }

          if (!hasFullTableEditPermission && columnData.edit) {
            permissionInputs.push({
              permissionObjectType: PermissionObjectType.TABLE_COLUMN,
              permissionType: PermissionType.EDIT_CELL,
              permissionObjectId: columnId,
            })
          }
        }

        // Only add tab permissions if no table-level permissions exist
        for (const tabId in childTablePerms.tabPermissions) {
          const tabData = get(childTablePerms.tabPermissions, tabId, undefined)
          if (!tabData) {
            continue
          }

          if (!hasFullTableViewPermission && tabData.view) {
            permissionInputs.push({
              permissionObjectType: PermissionObjectType.TABLE_TAB,
              permissionType: PermissionType.VIEW_TABLE_TAB_ROWS,
              permissionObjectId: tabId,
            })
          }
        }
      }
    }
  }
}

/**
 * Converts the form data for creating a role into an array of permission inputs, filtering out
 * redundant inputs for tables
 * @param formData The structured role form data
 * @param formV2Ids Set of form IDs that are Form V2 (for passing isFormV2 flag to backend)
 */
export function convertFormDataToPermissionInput(
  formData: StructuredRoleFormData,
  formV2Ids: Set<string> = new Set(),
): PermissionInput[] {
  const permissionInputs: PermissionInput[] = []
  for (const tableId in formData.tablePermissions) {
    const tableData = get(formData.tablePermissions, tableId, undefined)
    if (!tableData) {
      continue
    }

    convertParentTableFormDataToPermissionInputs(tableId, tableData, permissionInputs)

    // Create child table permissions
    processChildTablePermissions(tableData.childTables, permissionInputs, {
      includeAllPermissions: true,
    })
  }

  for (const formId in formData.formPermissions) {
    const formPermission = get(formData.formPermissions, formId, undefined)
    if (!formPermission) {
      continue
    }
    if (formPermission.submit) {
      const isFormV2 = formV2Ids.has(formId)
      permissionInputs.push({
        permissionObjectType: PermissionObjectType.FORM,
        permissionType: PermissionType.SUBMIT_FORM,
        permissionObjectId: formId,
        isFormV2: isFormV2 || undefined,
      })
    }
  }

  for (const workspaceId in formData.workspacePermissions) {
    const workspacePermission = get(formData.workspacePermissions, workspaceId, undefined)
    if (!workspacePermission) {
      continue
    }

    if (workspacePermission.edit) {
      permissionInputs.push({
        permissionObjectType: PermissionObjectType.WORKSPACE,
        permissionType: PermissionType.EDIT_WORKSAPCE,
        permissionObjectId: workspaceId,
      })
      permissionInputs.push({
        permissionObjectType: PermissionObjectType.WORKSPACE,
        permissionType: PermissionType.VIEW_WORKSPACE,
        permissionObjectId: workspaceId,
      })
    }

    if (workspacePermission.view && !workspacePermission.edit) {
      permissionInputs.push({
        permissionObjectType: PermissionObjectType.WORKSPACE,
        permissionType: PermissionType.VIEW_WORKSPACE,
        permissionObjectId: workspaceId,
      })
    }
  }

  for (const manualTriggerId in formData.manualTriggerPermissions) {
    const manualTriggerPermission = get(
      formData.manualTriggerPermissions,
      manualTriggerId,
      undefined,
    )
    if (!manualTriggerPermission) {
      continue
    }

    if (manualTriggerPermission.submit) {
      permissionInputs.push({
        permissionObjectType: PermissionObjectType.MANUAL_TRIGGER,
        permissionType: PermissionType.SUBMIT_MANUAL_TRIGGER,
        permissionObjectId: manualTriggerId,
      })
    }
  }

  for (const embedId in formData.embedPermissions) {
    const embedPermission = get(formData.embedPermissions, embedId, undefined)
    if (!embedPermission) {
      continue
    }

    if (embedPermission.view) {
      permissionInputs.push({
        permissionObjectType: PermissionObjectType.EMBED,
        permissionType: PermissionType.VIEW_EMBED,
        permissionObjectId: embedId,
      })
    }
  }

  for (const endpointId in formData.endpointPermissions) {
    if (get(formData.endpointPermissions, endpointId, undefined)?.call === true) {
      permissionInputs.push({
        permissionObjectType: PermissionObjectType.ENDPOINT,
        permissionType: PermissionType.CALL_ENDPOINT,
        permissionObjectId: endpointId,
      })
    }
  }

  return permissionInputs
}

/**
 * Type-safe table permission handlers - each handler only receives the specific permission type it can handle
 */
export const fullTablePermissionHandlers = {
  [PermissionType.VIEW_ALL_TABLE_CELLS]: (
    schema: TableFullPermissionFormData | ChildTablePermissionsFormData,
  ) => {
    for (const columnId in schema.columnPermissions) {
      schema.columnPermissions[columnId].view = true
    }
    for (const tabId in schema.tabPermissions) {
      schema.tabPermissions[tabId].view = true
    }
  },

  [PermissionType.EDIT_ALL_TABLE_CELLS]: (
    schema: TableFullPermissionFormData | ChildTablePermissionsFormData,
  ) => {
    for (const columnId in schema.columnPermissions) {
      schema.columnPermissions[columnId].view = true
      schema.columnPermissions[columnId].edit = true
    }
    for (const tabId in schema.tabPermissions) {
      schema.tabPermissions[tabId].view = true
    }
  },
}

export const generalTablePermissionHandlers = {
  [PermissionType.ADD_ROW]: (schema: TableFullPermissionFormData) => {
    schema.tablePermissions.addRow = true
  },

  [PermissionType.DELETE_ROW]: (schema: TableFullPermissionFormData) => {
    schema.tablePermissions.deleteRow = true
  },

  [PermissionType.CONFIGURE_TABLE_TABS]: (schema: TableFullPermissionFormData) => {
    schema.tablePermissions.configureTabs = true
  },

  [PermissionType.CONFIGURE_PIVOT_TABLES]: (schema: TableFullPermissionFormData) => {
    schema.tablePermissions.configurePivotTables = true
  },
}

export const columnPermissionHandlers = {
  [PermissionType.VIEW_COLUMN]: (
    schema: TableFullPermissionFormData | ChildTablePermissionsFormData,
    permission: TableColumnPermission,
  ) => {
    // Check top level for both parent and child table permissions
    const columnPermission = get(schema.columnPermissions, permission.tableColumn.id, undefined)
    if (columnPermission) {
      columnPermission.view = true
    }

    // If parent table, check the child tables for this column also
    if (tableFullPermissionFormSchema.isValidSync(schema)) {
      for (const childTableId in schema.childTables) {
        const childTablePermission = get(schema.childTables, childTableId, undefined)
        if (!childTablePermission) {
          continue
        }

        const childColumnPermission = get(
          childTablePermission.columnPermissions,
          permission.tableColumn.id,
          undefined,
        )
        if (childColumnPermission) {
          childColumnPermission.view = true
        }
      }
    }
  },

  [PermissionType.EDIT_CELL]: (
    schema: TableFullPermissionFormData | ChildTablePermissionsFormData,
    permission: TableColumnPermission,
  ) => {
    // Check top level for both parent and child table permissions
    const columnPermission = get(schema.columnPermissions, permission.tableColumn.id, undefined)
    if (columnPermission) {
      columnPermission.edit = true
      columnPermission.view = true
    }

    // If parent table, check the child tables for this column also
    if (tableFullPermissionFormSchema.isValidSync(schema)) {
      for (const childTableId in schema.childTables) {
        const childTablePermission = get(schema.childTables, childTableId, undefined)
        if (!childTablePermission) {
          continue
        }

        const childColumnPermission = get(
          childTablePermission.columnPermissions,
          permission.tableColumn.id,
          undefined,
        )

        if (childColumnPermission) {
          childColumnPermission.edit = true
          childColumnPermission.view = true
        }
      }
    }
  },
}

export const tabPermissionHandlers = {
  [PermissionType.VIEW_TABLE_TAB_ROWS]: (
    schema: TableFullPermissionFormData | ChildTablePermissionsFormData,
    permission: TableTabPermission,
  ) => {
    // Check top level for both parent and child table permissions
    const tabPermission = get(schema.tabPermissions, permission.tableTab.id, undefined)
    if (tabPermission) {
      tabPermission.view = true
    }

    // If parent table, check the child tables for this tab also
    if (tableFullPermissionFormSchema.isValidSync(schema)) {
      // Also check child tables for this tab
      for (const childTableId in schema.childTables) {
        const childTablePermission = get(schema.childTables, childTableId, undefined)
        if (!childTablePermission) {
          continue
        }

        const childTabPermission = get(
          childTablePermission.tabPermissions,
          permission.tableTab.id,
          undefined,
        )
        if (childTabPermission) {
          childTabPermission.view = true
        }
      }
    }
  },
}

/**
 * Creates the initial role form data with all permissions set to false
 */
export function createInitialRoleFormData(
  tables: PermissionsTabTable[],
  forms: PermissionsTabFormUnified[],
  manualTriggers: ManualTrigger[],
  workspaces: { id: string; name: string }[],
  embeds: PermissionsTabEmbed[],
  endpoints: PermissionsTabEndpoint[],
  roleName?: string,
): StructuredRoleFormData {
  const nonChildTables = tables.filter((table) => !table.parentTable)
  return {
    name: roleName ?? '',
    tablePermissions: nonChildTables.reduce(
      (acc, table) => ({
        ...acc,
        [table.id]: createInitialTablePermissionSchema(table),
      }),
      {},
    ),
    formPermissions: forms.reduce(
      (acc, form) => ({
        ...acc,
        [form.id]: { submit: false },
      }),
      {},
    ),
    manualTriggerPermissions: manualTriggers.reduce(
      (acc, trigger) => ({
        ...acc,
        [trigger.id]: { submit: false },
      }),
      {},
    ),
    workspacePermissions: workspaces.reduce(
      (acc, workspace) => ({
        ...acc,
        [workspace.id]: { view: false, edit: false },
      }),
      {},
    ),
    embedPermissions: embeds.reduce(
      (acc, embed) => ({
        ...acc,
        [embed.id]: { view: false },
      }),
      {},
    ),
    endpointPermissions: Object.fromEntries(
      endpoints.map((endpoint) => [endpoint.id, { call: false }]),
    ),
  }
}

/**
 * Converts the role and its permissions into role form data
 */
export function convertPermissionsToFormData(
  rolePermissions: Permission[],
  tables: PermissionsTabTable[],
  forms: PermissionsTabFormUnified[],
  manualTriggers: ManualTrigger[],
  workspaces: { id: string; name: string }[],
  embeds: PermissionsTabEmbed[],
  endpoints: PermissionsTabEndpoint[],
  roleName: string,
): StructuredRoleFormData {
  const childTables = tables.filter((table) => table.parentTable)
  // Map of child table id to parent table id
  const parentTableMap: Map<string, string | undefined> = new Map(
    childTables.map((table) => [table.id, table.parentTable?.id]),
  )

  // Initialize with default structure
  const formData = createInitialRoleFormData(
    tables,
    forms,
    manualTriggers,
    workspaces,
    embeds,
    endpoints,
    roleName,
  )

  // Helper function to find parent table permission and child table permission
  const findTablePermissions = (
    tableId: string,
  ): TableFullPermissionFormData | ChildTablePermissionsFormData | null => {
    // If it's a parent table
    const parentPermission = get(formData.tablePermissions, tableId, undefined)
    if (parentPermission) {
      return parentPermission
    }

    // If it's a child table, find its parent
    const parentTableId = parentTableMap.get(tableId)
    if (parentTableId) {
      const parentPermission = get(formData.tablePermissions, parentTableId, undefined)

      const childPermission = get(parentPermission?.childTables, tableId, undefined)
      if (childPermission) {
        return childPermission
      }
    }

    // Could not find the table permission so return null
    return null
  }

  const validPermissions = filterByType(rolePermissions, isPermission)

  validPermissions.forEach((permission) => {
    if (isFullTablePermission(permission)) {
      const handler = fullTablePermissionHandlers[permission.permissionType]
      const schema = findTablePermissions(permission.table.id)

      if (schema) {
        handler(schema)
      }
    } else if (isGeneralTablePermission(permission)) {
      const handler = generalTablePermissionHandlers[permission.permissionType]
      const schema = findTablePermissions(permission.table.id)
      // Child table general table permissions are inherited from the parent table so only need
      // to check the top level table permissions
      if (schema && 'tablePermissions' in schema) {
        handler(schema)
      }
    } else if (isTableColumnPermission(permission)) {
      const handler = columnPermissionHandlers[permission.permissionType]
      const schema = findTablePermissions(permission.tableColumn.table.id)
      if (schema) {
        handler(schema, permission)
      }
    } else if (isTableTabPermission(permission)) {
      const schema = findTablePermissions(permission.tableTab.table.id)
      if (schema) {
        const handler = tabPermissionHandlers[permission.permissionType]
        handler(schema, permission)
      }
    } else if (isFormPermission(permission)) {
      const formId = permission.form?.id ?? permission.formV2?.id
      if (formId) {
        const formPermission = get(formData.formPermissions, formId, undefined)
        if (formPermission) {
          switch (permission.permissionType) {
            // [OS-381] v8 does not see the enum value narrowing.
            // oxlint-disable-next-line @typescript-eslint/no-unnecessary-condition
            case PermissionType.SUBMIT_FORM:
              formPermission.submit = true
              break
          }
        }
      }
    } else if (isWorkspacePermission(permission)) {
      const workspacePermission = get(
        formData.workspacePermissions,
        permission.workspace.id,
        undefined,
      )
      if (workspacePermission) {
        switch (permission.permissionType) {
          case PermissionType.VIEW_WORKSPACE:
            workspacePermission.view = true
            break
          case PermissionType.EDIT_WORKSAPCE:
            workspacePermission.edit = true
            workspacePermission.view = true
            break
        }
      }
    } else if (isManualTriggerPermission(permission)) {
      const manualTriggerPermission = get(
        formData.manualTriggerPermissions,
        permission.manualTrigger.id,
        undefined,
      )
      if (manualTriggerPermission) {
        switch (permission.permissionType) {
          // [OS-381] v8 does not see the enum value narrowing.
          // oxlint-disable-next-line @typescript-eslint/no-unnecessary-condition
          case PermissionType.SUBMIT_MANUAL_TRIGGER:
            manualTriggerPermission.submit = true
            break
        }
      }
    } else if (isEmbedPermission(permission)) {
      // Grants on embeds that have since moved into a dashboard are not listed in the form (they
      // inherit from the dashboard), so `get` may miss. The saved permission is then dropped on
      // the next save, which is fine: the dashboard grant is the path that applies now.
      // VIEW_EMBED is the only embed permission type, already narrowed by isEmbedPermission.
      const embedPermission = get(formData.embedPermissions, permission.embed.id, undefined)
      if (embedPermission) {
        embedPermission.view = true
      }
    } else if (isEndpointPermission(permission)) {
      // A grant on an endpoint since made internal is not listed, so it drops on the next save —
      // which is right: apps can no longer reach it.
      const endpointPermission = get(
        formData.endpointPermissions,
        permission.endpoint.id,
        undefined,
      )
      if (endpointPermission) {
        endpointPermission.call = true
      }
    }
  })

  return formData
}

/**
 * Creates type-safe paths for accessing form fields related to form permissions
 */
export function createFormPermissionPaths(formId: string) {
  return {
    form: (field: FormPermissionType) => `formPermissions.${formId}.${field}` as const,
  }
}

/**
 * Creates type-safe paths for accessing form fields related to workspace permissions
 */
export function createWorkspacePermissionPaths(workspaceId: string) {
  return {
    workspace: (field: BasicPermissionType) =>
      `workspacePermissions.${workspaceId}.${field}` as const,
  }
}

/**
 * Creates type-safe paths for accessing form fields related to manual trigger permissions
 */
export function createManualTriggersPermissionPaths(manualTriggerId: string) {
  return {
    manualTrigger: (field: FormPermissionType) =>
      `manualTriggerPermissions.${manualTriggerId}.${field}` as const,
  }
}

/**
 * Creates the initial permission schema with all permissions set to false
 */
export function createInitialTablePermissionSchema(
  table: SimpleTable,
): TableFullPermissionFormData {
  return {
    tablePermissions: {
      addRow: false,
      deleteRow: false,
      configureTabs: false,
      configurePivotTables: false,
    },
    columnPermissions: sortBy(table.columns, (column) => column.order).reduce(
      (acc, column) => ({
        ...acc,
        [column.id]: {
          view: false,
          edit: false,
        },
      }),
      {},
    ),
    tabPermissions: sortBy(table.tableTabs, (tab) => tab.order).reduce(
      (acc, tab) => ({
        ...acc,
        [tab.id]: {
          view: false,
        },
      }),
      {},
    ),
    // Placeholder for child tables since they inherit tablePermissions from parent and we
    // are not updating their specific column and tab permissions when updating their parent
    // table permissions. We only need to keep track if they have full view or full edit permission
    // because those will be deleted in the backend and then need to be recreated
    childTables: table.childTables.reduce(
      (acc, childTable) => ({
        ...acc,
        [childTable.id]: {
          columnPermissions: sortBy(childTable.columns, (column) => column.order).reduce(
            (acc, column) => ({
              ...acc,
              [column.id]: {
                view: false,
                edit: false,
              },
            }),
            {},
          ),
          tabPermissions: sortBy(childTable.tableTabs, (tab) => tab.order).reduce(
            (acc, tab) => ({
              ...acc,
              [tab.id]: {
                view: false,
              },
            }),
            {},
          ),
        },
      }),
      {},
    ),
  }
}
